import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Trash2, Send, ShoppingCart, ArrowLeft, Plus, Minus, ChevronDown, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  'Belgie', 'Nederland', 'Deutschland', 'France', 'Luxembourg',
  'United Kingdom', 'Osterreich', 'Schweiz', 'Italia', 'Espana',
  'Portugal', 'Danmark', 'Sverige', 'Norge', 'Suomi', 'Polska',
  'Cesko', 'Magyarorszag', 'Romania', 'Bulgaria', 'Hrvatska',
  'Slovenija', 'Slovensko', 'Lietuva', 'Latvija', 'Eesti',
  'Ireland', 'Turkiye', 'United States', 'Canada', 'Maroc', 'Tunisie',
];

const CountrySelect = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const filtered = COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={ref} data-testid="quote-country">
      <button type="button" onClick={() => setOpen(!open)}
        className={`w-full px-3 py-2.5 border text-sm text-left flex items-center justify-between transition-colors bg-white ${open ? 'border-[#BE1622]' : 'border-gray-200'}`}>
        <span className={value ? 'text-slate-900' : 'text-slate-400'}>{value || placeholder}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg max-h-52 flex flex-col">
          <div className="flex items-center border-b border-gray-100 px-3 py-2">
            <Search className="w-3.5 h-3.5 text-slate-400 mr-2 flex-shrink-0" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="..." className="w-full text-sm focus:outline-none" autoFocus />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(c => (
              <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${value === c ? 'text-[#BE1622] font-medium bg-red-50/50' : 'text-slate-700'}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const OffertePage = () => {
  const { language } = useLanguage();
  const { items, updateItem, removeItem, clearCart } = useCart();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', company: '', btw: '', email: '', phone: '', country: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const getProductName = (item) => {
    const nameField = `productName${language === 'nl' ? '' : language.charAt(0).toUpperCase() + language.slice(1)}`;
    return item[nameField] || item.productName || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);

    const productLines = items.map((item, i) =>
      `${i + 1}. ${getProductName(item)} | ${item.orderType === 'truck' ? 'Vrachtwagen/Truck' : 'Palet/Pallet'} | Qty: ${item.quantity}`
    ).join('\n');

    try {
      await axios.post(`${API}/contact`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `[OFFERTE AANVRAAG]\n\nBTW: ${formData.btw}\nLand: ${formData.country}\n\n--- PRODUCTEN ---\n${productLines}\n\n--- BERICHT ---\n${formData.message}`
      });
      setSubmitted(true);
      clearCart();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const tx = {
    nl: { title: 'Offerte Aanvragen', back: 'Verder winkelen', empty: 'Uw offertelijst is leeg', emptyDesc: 'Voeg producten toe vanuit ons assortiment.', browse: 'Bekijk producten', products: 'PRODUCTEN', pallet: 'Palet', truck: 'Vrachtwagen', contact: 'UW GEGEVENS', firstName: 'Voornaam', lastName: 'Achternaam', company: 'Bedrijf', btw: 'BTW Nummer', email: 'E-mail', phone: 'Telefoon', country: 'Selecteer land', msg: 'Opmerkingen', submit: 'Offerte versturen', success: 'Uw offerteaanvraag is succesvol verzonden!', successDesc: 'Wij nemen zo snel mogelijk contact met u op.', newQuote: 'Nieuwe offerte' },
    en: { title: 'Request Quote', back: 'Continue shopping', empty: 'Your quote list is empty', emptyDesc: 'Add products from our range.', browse: 'Browse products', products: 'PRODUCTS', pallet: 'Pallet', truck: 'Truck', contact: 'YOUR DETAILS', firstName: 'First name', lastName: 'Last name', company: 'Company', btw: 'VAT Number', email: 'Email', phone: 'Phone', country: 'Select country', msg: 'Comments', submit: 'Submit quote request', success: 'Your quote request has been sent successfully!', successDesc: 'We will contact you as soon as possible.', newQuote: 'New quote' },
    fr: { title: 'Demander un Devis', back: 'Continuer vos achats', empty: 'Votre liste de devis est vide', emptyDesc: 'Ajoutez des produits depuis notre gamme.', browse: 'Voir les produits', products: 'PRODUITS', pallet: 'Palette', truck: 'Camion', contact: 'VOS COORDONNEES', firstName: 'Prenom', lastName: 'Nom', company: 'Entreprise', btw: 'Numero TVA', email: 'E-mail', phone: 'Telephone', country: 'Selectionnez un pays', msg: 'Remarques', submit: 'Envoyer la demande', success: 'Votre demande a ete envoyee avec succes!', successDesc: 'Nous vous contacterons dans les plus brefs delais.', newQuote: 'Nouveau devis' },
    de: { title: 'Angebot Anfordern', back: 'Weiter einkaufen', empty: 'Ihre Angebotsliste ist leer', emptyDesc: 'Fugen Sie Produkte aus unserem Sortiment hinzu.', browse: 'Produkte ansehen', products: 'PRODUKTE', pallet: 'Palette', truck: 'LKW', contact: 'IHRE DATEN', firstName: 'Vorname', lastName: 'Nachname', company: 'Unternehmen', btw: 'USt-IdNr.', email: 'E-Mail', phone: 'Telefon', country: 'Land auswahlen', msg: 'Bemerkungen', submit: 'Angebot absenden', success: 'Ihre Angebotsanfrage wurde erfolgreich gesendet!', successDesc: 'Wir werden uns so schnell wie moglich bei Ihnen melden.', newQuote: 'Neues Angebot' },
  }[language] || { title: 'Offerte', back: 'Terug', empty: 'Leeg', emptyDesc: '', browse: 'Producten', products: 'PRODUCTEN', pallet: 'Palet', truck: 'Vrachtwagen', contact: 'GEGEVENS', firstName: 'Voornaam', lastName: 'Achternaam', company: 'Bedrijf', btw: 'BTW', email: 'E-mail', phone: 'Telefoon', country: 'Land', msg: 'Bericht', submit: 'Verstuur', success: 'Verzonden!', successDesc: '', newQuote: 'Nieuw' };

  return (
    <div data-testid="offerte-page">
      <section className="bg-[#0F172A] pt-28 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">{tx.title}</h1>
        </div>
      </section>

      <section className="py-10 bg-white min-h-[60vh]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {submitted ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <Send className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">{tx.success}</h2>
              <p className="text-slate-500 text-sm mb-6">{tx.successDesc}</p>
              <Link to="/products" className="inline-flex items-center gap-2 bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-6 py-3 transition-all">
                {tx.newQuote}
              </Link>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingCart className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-900 mb-2">{tx.empty}</h2>
              <p className="text-slate-500 text-sm mb-6">{tx.emptyDesc}</p>
              <Link to="/products" className="inline-flex items-center gap-2 bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-6 py-3 transition-all">
                {tx.browse}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Product list */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-bold text-slate-400 tracking-[0.15em] uppercase">{tx.products} ({items.length})</p>
                  <Link to="/products" className="text-xs text-[#BE1622] hover:underline font-medium flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> {tx.back}
                  </Link>
                </div>

                <div className="border border-gray-200">
                  {items.map((item, index) => (
                    <div key={item.key} className={`flex items-center gap-4 p-4 ${index > 0 ? 'border-t border-gray-100' : ''}`} data-testid={`cart-item-${index}`}>
                      {/* Image */}
                      <div className="w-14 h-14 bg-[#F8FAFC] flex items-center justify-center flex-shrink-0">
                        <img src={item.imageUrl} alt="" className="max-w-full max-h-full object-contain" />
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{getProductName(item)}</p>
                      </div>

                      {/* Order type toggle */}
                      <div className="flex border border-gray-200 flex-shrink-0">
                        <button type="button" onClick={() => updateItem(item.key, { orderType: 'pallet' })}
                          className={`px-3 py-1.5 text-[10px] font-semibold transition-all ${item.orderType === 'pallet' ? 'bg-[#BE1622] text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                          {tx.pallet}
                        </button>
                        <button type="button" onClick={() => updateItem(item.key, { orderType: 'truck' })}
                          className={`px-3 py-1.5 text-[10px] font-semibold border-l border-gray-200 transition-all ${item.orderType === 'truck' ? 'bg-[#BE1622] text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                          {tx.truck}
                        </button>
                      </div>

                      {/* Quantity */}
                      <div className="flex items-center border border-gray-200 flex-shrink-0">
                        <button type="button" onClick={() => updateItem(item.key, { quantity: Math.max(1, item.quantity - 1) })} className="px-2 py-1.5 text-slate-400 hover:text-slate-900">
                          <Minus className="w-3 h-3" />
                        </button>
                        <input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(item.key, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                          className="w-12 text-center py-1.5 text-xs font-medium border-x border-gray-200 focus:outline-none" />
                        <button type="button" onClick={() => updateItem(item.key, { quantity: item.quantity + 1 })} className="px-2 py-1.5 text-slate-400 hover:text-slate-900">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Delete */}
                      <button type="button" onClick={() => removeItem(item.key)} className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0" data-testid={`remove-item-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact form */}
              <div className="mb-8">
                <p className="text-[10px] font-bold text-slate-400 tracking-[0.15em] uppercase mb-4">{tx.contact}</p>
                <div className="border border-gray-200 p-6 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" required placeholder={tx.firstName} value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-firstname" />
                    <input type="text" required placeholder={tx.lastName} value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-lastname" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" placeholder={tx.company} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-company" />
                    <input type="text" placeholder={tx.btw} value={formData.btw} onChange={(e) => setFormData({ ...formData, btw: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-btw" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="email" required placeholder={tx.email} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-email" />
                    <input type="tel" placeholder={tx.phone} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" data-testid="offerte-phone" />
                  </div>
                  <CountrySelect value={formData.country} onChange={(v) => setFormData({ ...formData, country: v })} placeholder={tx.country} />
                  <textarea placeholder={tx.msg} rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] resize-none" data-testid="offerte-message" />
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading} data-testid="offerte-submit"
                className="w-full bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] py-4 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send className="w-4 h-4" /> {tx.submit}</>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};
